<template>
    <v-row class="mb-0 mt-0">
        <v-col cols="6" class="text-start">
            <base-info-card :title="infoType + ' Contact Information'" color="primary">
                <v-row class="mb-1"
                    ><v-col class="ma-0" cols="12" sm="6">
                        <v-text-field
                            v-model="contact.firstName"
                            outlined
                            hide-details
                            dense
                            v-bind="$attrs"
                            label="First Name"
                        />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="contact.lastName"
                            outlined
                            hide-details
                            dense
                            v-bind="$attrs"
                            label="Last Name"
                        />
                    </v-col>
                </v-row>
                <v-text-field
                    v-model="contact.company"
                    class="mb-3"
                    hide-details
                    outlined
                    dense
                    v-bind="$attrs"
                    label="Company"
                />
                <v-text-field
                    v-model="contact.email"
                    class="mb-3"
                    hide-details
                    v-bind="$attrs"
                    label="Email"
                    outlined
                    dense
                />
                <base-tel-input
                    id="phone"
                    ref="phone"
                    v-model="phoneInput"
                    hide-details
                    class="tele-input phone-input"
                    style="color: white !important"
                    valid-characters-only
                    :dropdown-options="{
                        showDialCodeInSelection: true,
                        showFlags: true,
                        showDialCodeInList: true,
                        tabindex: 0
                    }"
                    :default-country="$auth.user.phoneCountryCode"
                    :input-options="{
                        placeholder: 'Phone',
                        maxlength: 25,
                        autocomplete: 'on',
                        name: 'telephone',
                        required: false,
                        autofocus: false,
                        id: '',
                        readonly: false,
                        tabindex: 0,
                        type: 'tel',
                        styleClasses: ''
                    }"
                    @update="assignPhone"
                />
            </base-info-card>
        </v-col>
        <v-col cols="6" class="text-right">
            <base-info-card :title="infoType + ' Address Information'" color="primary">
                <v-text-field
                    ref="address-line1"
                    v-model="address.line1"
                    class="mb-4"
                    outlined
                    dense
                    hide-details
                    v-bind="$attrs"
                    label="Address Line 1"
                />
                <v-text-field
                    ref="address-line2"
                    v-model="address.line2"
                    class="mb-3"
                    outlined
                    dense
                    hide-details
                    v-bind="$attrs"
                    label="Address Line 2"
                />
                <v-row>
                    <v-col cols="12" sm="6">
                        <v-autocomplete
                            ref="country"
                            v-model="address.country"
                            outlined
                            dense
                            hide-details
                            v-bind="$attrs"
                            :items="countries"
                            item-text="name"
                            item-value="countryCode"
                            label="Country"
                            placeholder="Country"
                            @change="countryChanged"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" class="pl-0">
                        <v-autocomplete
                            ref="state"
                            v-model="address.state"
                            outlined
                            dense
                            hide-details
                            v-bind="$attrs"
                            item-text="name"
                            item-value="stateCode"
                            :loading="statesLoading"
                            :disabled="statesLoading || !address.country"
                            :items="stateProvinces"
                            label="State/Province/Region"
                            placeholder="State"
                        />
                    </v-col>
                </v-row>
                <v-row class="ma-0 pa-0">
                    <v-col cols="12" sm="6" class="pl-0">
                        <v-text-field
                            ref="city"
                            v-model="address.city"
                            outlined
                            dense
                            hide-details
                            v-bind="$attrs"
                            label="City"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" class="pr-0 pl-0">
                        <v-text-field
                            ref="zip"
                            v-model="address.postalCode"
                            outlined
                            dense
                            hide-details
                            v-bind="$attrs"
                            label="ZIP / Postal Code"
                        />
                    </v-col>
                </v-row>
            </base-info-card>
        </v-col>
    </v-row>
</template>

<script>
import formRules from '@/utils/form_rules'

export default {
    name: 'InfoInput',
    props: {
        value: {
            type: Object,
            required: false,
            default: () => {}
        },
        infoType: {
            type: String,
            required: false,
            default: ``
        }
    },
    data() {
        return {
            statesLoading: false,
            citiesLoading: false,
            formRules,
            phoneInput: ``,
            contact: {
                company: ``,
                firstName: ``,
                lastName: ``,
                email: ``,
                phone: ``,
                phoneCountryCode: ``
            },
            address: {
                line1: ``,
                line2: ``,
                city: ``,
                state: ``,
                postalCode: ``
            }
        }
    },
    computed: {
        oidcBilling() {
            return this.$auth.user.address ? this.$auth.user.address.billing : {}
        },
        oidcShipping() {
            return this.$auth.user.address ? this.$auth.user.address.shipping : {}
        },
        countries() {
            return this.$store.getters[`location/getCachedCountries`]
        },

        cities() {
            return this.$store.getters[`location/getCachedCities`]({
                countryCode: this.address.country,
                stateCode: this.address.state
            })
        },
        stateProvinces() {
            return this.$store.getters[`location/getCachedStates`]({
                countryCode: this.address.country
            })
        }
    },
    watch: {
        'address.country': {
            immediate: true,
            async handler(v) {
                if (v) {
                    this.statesLoading = true
                    await this.$store.dispatch(`location/cacheStates`, {
                        countryCode: v
                    })
                    this.statesLoading = false
                }
            }
        },
        value: {
            immediate: true,
            deep: true,
            handler(v) {
                if (v) {
                    this.contact = v.contact
                    this.address = v.address
                }
            }
        }
    },
    async mounted() {
        await this.$store.dispatch(`location/cacheStates`, {
            countryCode: this.address.country
        })
    },
    methods: {
        async countryChanged(v) {
            if (v) {
                this.statesLoading = true
                try {
                    await this.$store.dispatch(`location/cacheStates`, {
                        countryCode: v
                    })
                    this.statesLoading = false
                } catch {
                    this.statesLoading = false
                }
            } else this.address.state = ``
        },

        async getCities(event) {
            this.citiesLoading = true
            try {
                const [countryCode, stateCode] = event
                await this.$store.dispatch(`location/cacheCities`, {
                    countryCode,
                    stateCode
                })
            } catch {
                this.citiesLoading = false
            } finally {
                this.citiesLoading = false
            }
        },
        assignPhone(a) {
            // eslint-disable-next-line no-unused-vars
            const [input, phoneObject] = a
            this.phoneValid = phoneObject.nationalNumber ? !!phoneObject.valid : true
            this.contact.phone = phoneObject.nationalNumber
            this.contact.phoneCountryCode = phoneObject.countryCode
        }
    }
}
</script>
